import {
  SHRED_AWARD_IDS,
  SHRED_BRACKETS,
  SHRED_ROOKIE_BASELINE_SESSIONS,
  SHRED_AWARD_ORDER,
  SHRED_BASELINE_DAYS,
  SHRED_CHALLENGE_ID,
  SHRED_DAY_COUNT,
  SHRED_FEED_TYPES,
  SHRED_MIN_BASELINE_SESSIONS,
  SHRED_MIN_SESSIONS,
  SHRED_STATUS,
  SHRED_VALUE_UNITS,
} from './constants';

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const DEMO_SEED = 11;
const DEMO_TIMEZONE = 'America/Denver';
const DEMO_LIVE_ELAPSED_DAYS = 6.4;
const DEMO_ENDED_AGO_DAYS = 2;
const DEMO_UPCOMING_IN_DAYS = 3.2;

export const DEMO_ME_MODES = {
  MID: 'mid',
  TOP: 'top',
  CHASING: 'chasing',
  NEW: 'new',
  NONE: 'none',
};

const DEMO_MID_ID = 'demo-12';
const DEMO_ROOKIE_ME_ID = 'demo-r1';

const USERNAMES = [
  'sprintwolf', 'nightowl88', 'crimsontide', 'pixelpump', 'ironwillow', 'tempo_tess', 'lowgearleo', 'ember_ada',
  'quietstorm', 'maplemuscle', 'rowdyrhea', 'kettlekat', 'boltbarbara', 'driftcoach', 'sunriseset', 'plankpilot',
  'gritgus', 'moonlift', 'repricecake', 'cobaltcarl', 'tidalweights', 'hexhamstring', 'flexfox', 'granitegrace',
  'halobell', 'jetstreamjo', 'oakandiron', 'pacepenguin', 'summitsam', 'voltvera',
];

const ROOKIE_NAMES = ['newbiename', 'firstflex', 'freshpump', 'gradeone', 'wetpaintwalt', 'dayonedana'];
const ROOKIE_LEVELS = [3, 8, 12, 5, 16, 9];

const LEVELS = [
  52, 25, 50, 54, 21, 44, 35, 65, 78, 78, 84, 85, 63, 65, 65, 77, 66, 66, 87, 97, 41, 75, 95, 95, 37,
  60, 76, 48, 30, 100,
];

// mulberry32: tiny seeded PRNG so the fixture is identical on every load.
const createRandom = (seed) => {
  let state = seed >>> 0;
  return () => {
    state += 0x6d2b79f5;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const dayKeyFor = (day) => `2026-10-${String(20 + day).padStart(2, '0')}`;

let cachedMembers = null;

export const buildDemoMembers = () => {
  if (cachedMembers) {
    return cachedMembers;
  }
  const random = createRandom(DEMO_SEED);
  const gaussian = () => (random() + random() + random() + random() - 2) / 0.58;
  cachedMembers = USERNAMES.map((username, index) => {
    const level = LEVELS[index];
    const baselineAvgWork = Math.round(15000 + level * 640 + random() * 6000);
    const trend = 0.04 + gaussian() * 0.11;
    // Every sixth member plays rarely so the board always has a "still qualifying" group.
    const dailyOdds = index % 6 === 4 ? 0.2 : 0.3 + random() * 0.38;
    const paceBase = 150 + random() * 250 + level * 1.5;
    const ormBase = 90 + level * 1.4 + random() * 40;
    const sessions = [];
    for (let day = 1; day <= SHRED_DAY_COUNT; day += 1) {
      if (random() < dailyOdds) {
        sessions.push({
          day,
          work: Math.round(baselineAvgWork * (1 + trend + gaussian() * 0.08)),
          reps: Math.round(paceBase * (0.85 + random() * 0.3)),
        });
      }
    }
    return {
      id: `demo-${index + 1}`,
      username,
      level,
      baselineAvgWork,
      baselineSessions: 3 + Math.floor(random() * 38),
      ormBase,
      sessions,
    };
  });
  ROOKIE_NAMES.forEach((username, i) => {
    const level = ROOKIE_LEVELS[i];
    const baseline = Math.round(14000 + level * 700 + random() * 5000);
    const dailyOdds = i % 2 === 0 ? 0.55 : 0.25;
    const sessions = [];
    for (let day = 1; day <= SHRED_DAY_COUNT; day += 1) {
      if (random() < dailyOdds) {
        // Later sessions trend up as a new member finds their feet.
        const growth = 1 + Math.min(day, 8) * 0.025 + gaussian() * 0.06;
        sessions.push({
          day,
          work: Math.round(baseline * growth),
          reps: Math.round(160 + random() * 160),
        });
      }
    }
    cachedMembers.push({
      id: `demo-r${i + 1}`,
      username,
      level,
      rookie: true,
      baselineAvgWork: 0,
      baselineSessions: i % 3,
      ormBase: 0,
      sessions,
    });
  });
  return cachedMembers;
};

const isRookieMember = (member) => !!member.rookie;

const buildStandings = (members, day) => {
  const rows = members
    .map((member) => {
      const played = member.sessions.filter((s) => s.day <= day);
      if (played.length === 0) {
        return null;
      }
      const pace = played.reduce((sum, s) => sum + s.reps, 0) / played.length;
      const mean = (list) => list.reduce((sum, s) => sum + s.work, 0) / list.length;
      const base = {
        id: member.id,
        username: member.username,
        level: member.level,
        bracket: isRookieMember(member) ? SHRED_BRACKETS.ROOKIE : SHRED_BRACKETS.REGULAR,
        sessions: played.length,
        baselineSessions: member.baselineSessions,
        qualified: played.length >= SHRED_MIN_SESSIONS,
        pace: Math.round(pace),
      };
      if (isRookieMember(member)) {
        const early = played.slice(0, SHRED_ROOKIE_BASELINE_SESSIONS);
        const later = played.slice(SHRED_ROOKIE_BASELINE_SESSIONS);
        const baselineAvg = mean(early);
        const hasScore = played.length > SHRED_ROOKIE_BASELINE_SESSIONS;
        return {
          ...base,
          baselineAvgWork: Math.round(baselineAvg),
          avgWork: hasScore ? Math.round(mean(later)) : 0,
          improvementPct: hasScore ? Number(((mean(later) / baselineAvg - 1) * 100).toFixed(1)) : 0,
          // The contract requires 3+ sessions before a rookie can rank.
          qualified: base.qualified && hasScore,
          efficiency: null,
        };
      }
      const avgWork = mean(played);
      return {
        ...base,
        avgWork: Math.round(avgWork),
        baselineAvgWork: member.baselineAvgWork,
        improvementPct: Number(((avgWork / member.baselineAvgWork - 1) * 100).toFixed(1)),
        efficiency: Number((avgWork / member.ormBase).toFixed(1)),
      };
    })
    .filter(Boolean);
  const isRookieRow = (r) => r.bracket === SHRED_BRACKETS.ROOKIE;
  const rankBracket = (rookie) => {
    const bracketRows = rows.filter((r) => isRookieRow(r) === rookie);
    const qualified = bracketRows.filter((r) => r.qualified).sort((a, b) => b.improvementPct - a.improvementPct);
    const chasing = bracketRows
      .filter((r) => !r.qualified)
      .sort((a, b) => b.sessions - a.sessions || b.improvementPct - a.improvementPct);
    qualified.forEach((row, index) => {
      // eslint-disable-next-line no-param-reassign
      row.rank = index + 1;
    });
    chasing.forEach((row) => {
      // eslint-disable-next-line no-param-reassign
      row.rank = null;
    });
    return [...qualified, ...chasing];
  };
  return [...rankBracket(false), ...rankBracket(true)];
};

const rankMap = (standings) => {
  const map = {};
  standings.forEach((row) => {
    if (row.rank !== null) {
      map[row.id] = row.rank;
    }
  });
  return map;
};

const buildAwards = (standings) => {
  const qualified = standings.filter((row) => row.qualified && row.bracket === SHRED_BRACKETS.REGULAR);
  const rookieQualified = standings.filter((row) => row.qualified && row.bracket === SHRED_BRACKETS.ROOKIE);
  const levels = qualified.map((r) => r.level).sort((a, b) => a - b);
  const median = levels.length ? levels[Math.floor(levels.length / 2)] : 0;
  const top3 = (rows, valueOf) =>
    rows.slice(0, 3).map((row, index) => ({
      rank: index + 1,
      id: row.id,
      username: row.username,
      level: row.level,
      value: valueOf(row),
    }));
  const by = (key) => (a, b) => b[key] - a[key];
  const defs = {
    [SHRED_AWARD_IDS.SHRED_CHAMPION]: {
      title: 'Shred Champion',
      description: 'Biggest improvement against your own baseline.',
      valueUnit: SHRED_VALUE_UNITS.PCT,
      rows: [...qualified].sort(by('improvementPct')),
      value: (r) => r.improvementPct,
      reward: { title: '"Shredoween Champion" title + 2 weeks of MEP Meals + $100 gift card', items: ['"Shredoween Champion" profile title (never sold again)', '2 weeks of MEP Meals', '$100 recovery gift card'] },
    },
    [SHRED_AWARD_IDS.UNDERDOG]: {
      title: 'Underdog',
      description: 'Best improvement among members below the median level.',
      valueUnit: SHRED_VALUE_UNITS.PCT,
      rows: qualified.filter((r) => r.level < median).sort(by('improvementPct')),
      value: (r) => r.improvementPct,
      reward: { title: '"Shred Underdog" title + Gear Credits', items: ['"Shred Underdog" profile title', 'Gear Credits'] },
    },
    [SHRED_AWARD_IDS.PACE_KING]: {
      title: 'Pace King',
      description: 'Most reps per session.',
      valueUnit: SHRED_VALUE_UNITS.REPS,
      rows: [...qualified].sort(by('pace')),
      value: (r) => r.pace,
      reward: { title: '"Pace King" title + 50,000 XP', items: ['"Pace King" profile title', '50,000 XP'] },
    },
    [SHRED_AWARD_IDS.IRON_ENGINE]: {
      title: 'Iron Engine',
      description: 'Average work per session relative to your own strength.',
      valueUnit: SHRED_VALUE_UNITS.INDEX,
      rows: [...qualified].sort(by('efficiency')),
      value: (r) => r.efficiency,
      reward: { title: '"Iron Engine" title + Exercise Gear', items: ['"Iron Engine" profile title', 'Exercise Gear'] },
    },
    [SHRED_AWARD_IDS.IRON_HABIT]: {
      title: 'Iron Habit',
      description: 'Most full-length sessions with 20+ minutes of active play.',
      valueUnit: SHRED_VALUE_UNITS.SESSIONS,
      rows: [...qualified].sort((a, b) => b.sessions - a.sessions || b.improvementPct - a.improvementPct),
      value: (r) => r.sessions,
      reward: { title: '"Iron Habit" title + Streak Pass bundle', items: ['"Iron Habit" profile title', 'Streak Pass bundle'] },
    },
    [SHRED_AWARD_IDS.ROOKIE_STAR]: {
      title: 'Rookie Star',
      description: 'Best improvement in the Rookie bracket.',
      valueUnit: SHRED_VALUE_UNITS.PCT,
      rows: [...rookieQualified].sort(by('improvementPct')),
      value: (r) => r.improvementPct,
      reward: { title: '"Rookie Star" title + Starter Gear', items: ['"Rookie Star" profile title', 'Starter Gear'] },
    },
  };
  return SHRED_AWARD_ORDER.map((id) => {
    const def = defs[id];
    return {
      id,
      title: def.title,
      description: def.description,
      valueUnit: def.valueUnit,
      winners: top3(def.rows, def.value),
      reward: def.reward,
    };
  });
};

const buildFeed = (members, upToDay) => {
  const feed = [];
  let previous = [];
  let previousRanks = {};
  for (let day = 1; day <= upToDay; day += 1) {
    const standings = buildStandings(members, day);
    const ranks = rankMap(standings);
    const priorRanks = previousRanks;
    const events = [];
    const dayWork = members.reduce(
      (sum, m) => sum + m.sessions.filter((s) => s.day === day).reduce((s2, s) => s2 + s.work, 0),
      0
    );
    const daySessions = members.reduce((sum, m) => sum + m.sessions.filter((s) => s.day === day).length, 0);
    events.push({ day, type: SHRED_FEED_TYPES.DAY_RECAP, sessions: daySessions, totalWork: dayWork });

    const isRegular = (r) => r.bracket === SHRED_BRACKETS.REGULAR;
    const leader = standings.find((r) => r.rank === 1 && isRegular(r));
    const previousLeader = previous.find((r) => r.rank === 1 && isRegular(r));
    if (leader && (!previousLeader || previousLeader.id !== leader.id)) {
      events.push({
        day,
        type: SHRED_FEED_TYPES.NEW_LEADER,
        id: leader.id,
        username: leader.username,
        rank: 1,
        improvementPct: leader.improvementPct,
      });
    }
    standings
      .filter((r) => isRegular(r) && r.rank !== null && priorRanks[r.id] && priorRanks[r.id] - r.rank >= 3)
      .sort((a, b) => priorRanks[b.id] - b.rank - (priorRanks[a.id] - a.rank))
      .slice(0, 2)
      .forEach((r) => {
        events.push({
          day,
          type: SHRED_FEED_TYPES.CLIMBER,
          id: r.id,
          username: r.username,
          rank: r.rank,
          delta: priorRanks[r.id] - r.rank,
        });
      });
    standings
      .filter((r) => r.qualified && priorRanks[r.id] === undefined)
      .forEach((r) => {
        events.push({
          day,
          type: SHRED_FEED_TYPES.QUALIFIED,
          id: r.id,
          username: r.username,
          bracket: r.bracket,
          rank: r.rank,
          sessions: r.sessions,
        });
      });
    // Within a day, newest-first reads recap, then headline moves, then qualifiers.
    feed.unshift(...events);
    previous = standings;
    previousRanks = ranks;
  }
  return feed.slice(0, 60);
};

const buildCrew = (members, upToDay) => {
  const all = members.flatMap((m) => m.sessions);
  const fullTotal = all.reduce((sum, s) => sum + s.work, 0);
  const goalWork = Math.round((fullTotal * 1.04) / 10000) * 10000;
  const byDay = [];
  for (let day = 1; day <= upToDay; day += 1) {
    const sessions = all.filter((s) => s.day === day);
    byDay.push({
      day,
      dayKey: dayKeyFor(day),
      totalWork: sessions.reduce((sum, s) => sum + s.work, 0),
      sessions: sessions.length,
    });
  }
  const upTo = all.filter((s) => s.day <= upToDay);
  return {
    totalWork: upTo.reduce((sum, s) => sum + s.work, 0),
    goalWork,
    sessions: upTo.length,
    participants: members.filter((m) => m.sessions.some((s) => s.day <= upToDay)).length,
    byDay,
  };
};

const buildMe = (mode, standings) => {
  if (mode === DEMO_ME_MODES.NONE) {
    return null;
  }
  if (mode === DEMO_ME_MODES.NEW) {
    const rookie = standings.find((r) => r.id === DEMO_ROOKIE_ME_ID) || null;
    return rookie
      ? { id: rookie.id, username: rookie.username, eligible: true, reason: 'rookie', entry: rookie }
      : { id: DEMO_ROOKIE_ME_ID, username: ROOKIE_NAMES[0], eligible: true, reason: 'rookie' };
  }
  let entry = null;
  if (mode === DEMO_ME_MODES.TOP) {
    entry = standings.find((r) => r.rank === 1) || null;
  } else if (mode === DEMO_ME_MODES.CHASING) {
    entry = standings.find((r) => !r.qualified) || null;
  } else {
    entry = standings.find((r) => r.id === DEMO_MID_ID) || null;
  }
  if (!entry) {
    return { id: DEMO_MID_ID, username: USERNAMES[11], eligible: true };
  }
  return { id: entry.id, username: entry.username, eligible: true, entry };
};

export const getDemoScenario = (demoValue) => {
  if (demoValue === SHRED_STATUS.ENDED) return SHRED_STATUS.ENDED;
  if (demoValue === SHRED_STATUS.UPCOMING) return SHRED_STATUS.UPCOMING;
  return SHRED_STATUS.LIVE;
};

export const buildDemoResponse = ({
  asOfDay,
  scenario = SHRED_STATUS.LIVE,
  me = DEMO_ME_MODES.MID,
  nowMs = Date.now(),
} = {}) => {
  const members = buildDemoMembers();
  let startMs;
  if (scenario === SHRED_STATUS.ENDED) {
    startMs = nowMs - (SHRED_DAY_COUNT + DEMO_ENDED_AGO_DAYS) * MS_PER_DAY;
  } else if (scenario === SHRED_STATUS.UPCOMING) {
    startMs = nowMs + DEMO_UPCOMING_IN_DAYS * MS_PER_DAY;
  } else {
    startMs = nowMs - DEMO_LIVE_ELAPSED_DAYS * MS_PER_DAY;
  }
  const endMs = startMs + SHRED_DAY_COUNT * MS_PER_DAY;
  const currentDay =
    scenario === SHRED_STATUS.UPCOMING
      ? 0
      : scenario === SHRED_STATUS.ENDED
      ? SHRED_DAY_COUNT
      : Math.floor((nowMs - startMs) / MS_PER_DAY) + 1;

  const hasAsOfDay = asOfDay !== null && asOfDay !== undefined && Number(asOfDay) > 0;
  const requested = hasAsOfDay ? Math.round(Number(asOfDay)) : currentDay;
  const day = scenario === SHRED_STATUS.UPCOMING ? 0 : Math.min(Math.max(requested, 1), currentDay);

  const standings = day === 0 ? [] : buildStandings(members, day);
  const previousStandings = day > 1 ? buildStandings(members, day - 1) : [];
  const previousRanks = rankMap(previousStandings);
  const entries = standings.map((row) => ({
    ...row,
    previousRank: row.rank !== null && previousRanks[row.id] ? previousRanks[row.id] : null,
  }));
  const meBlock = buildMe(me, entries);

  return {
    challenge: {
      id: SHRED_CHALLENGE_ID,
      name: 'Shredoween',
      status: scenario,
      startMs,
      endMs,
      timezone: DEMO_TIMEZONE,
      dayCount: SHRED_DAY_COUNT,
      currentDay,
      minSessions: SHRED_MIN_SESSIONS,
      baselineDays: SHRED_BASELINE_DAYS,
      minBaselineSessions: SHRED_MIN_BASELINE_SESSIONS,
      rookieBaselineSessions: SHRED_ROOKIE_BASELINE_SESSIONS,
      rewards: {
        participation: 'Everyone who qualifies earns the "Shredoween Survivor" profile title.',
        crewGoal: 'Fill the shared crew goal and everyone who played gets a Crew Boost: 150,000 XP and the "Shredoween Crew" profile title.',
        podium: 'Podium finishers also get a friend invite code.',
      },
    },
    generatedAtMs: nowMs,
    entries,
    awards: day === 0 ? [] : buildAwards(entries),
    crew: day === 0 ? { totalWork: 0, goalWork: buildCrew(members, 0).goalWork, sessions: 0, participants: 0, byDay: [] } : buildCrew(members, day),
    feed: day === 0 ? [] : buildFeed(members, day),
    me: meBlock,
  };
};
